import {useCallback, useEffect, useRef} from "react"
import {useDispatch} from "react-redux"
import {useSearchParams} from "react-router-dom"
import {setSelectedBranchId, useSelectedApp} from "./appSlice"
import {AccessPolicy, isBranchAllowed} from "../access"

export const BRANCH_URL_PARAM = "branch"

export const getBranchIdFromSearch = (search: string) =>
    new URLSearchParams(search).get(BRANCH_URL_PARAM)?.trim() || undefined

/**
 * Keeps the header branch in the query string on every screen, so reloads, Back and shared links
 * restore it. An explicit `?branch=` wins over the store; a URL without it inherits the store value.
 */
export const useBranchUrlSync = (policy: AccessPolicy) => {
    const dispatch = useDispatch()
    const {selectedBranchId} = useSelectedApp()
    const [searchParams, setSearchParams] = useSearchParams()
    const urlBranchId = searchParams.get(BRANCH_URL_PARAM)?.trim() || undefined
    const pendingSelection = useRef<{branchId?: string} | null>(null)

    useEffect(() => {
        if (!policy.isReady) return
        // Router navigation may commit after Redux. Do not restore the old URL branch in between.
        if (pendingSelection.current) {
            if (urlBranchId !== pendingSelection.current.branchId) return
            const branchId = pendingSelection.current.branchId
            pendingSelection.current = null
            if (!branchId || isBranchAllowed(branchId, policy)) {
                if (selectedBranchId !== branchId) dispatch(setSelectedBranchId(branchId))
                return
            }
        }
        const requestedBranchId = urlBranchId || selectedBranchId
        if (requestedBranchId && !isBranchAllowed(requestedBranchId, policy)) {
            pendingSelection.current = {branchId: undefined}
            dispatch(setSelectedBranchId(undefined))
            setSearchParams((currentParams) => {
                const nextParams = new URLSearchParams(currentParams)
                nextParams.delete(BRANCH_URL_PARAM)
                nextParams.delete("page")
                return nextParams
            }, {replace: true})
            return
        }
        if (urlBranchId) {
            if (urlBranchId !== selectedBranchId) dispatch(setSelectedBranchId(urlBranchId))
            return
        }

        if (selectedBranchId) {
            setSearchParams((currentParams) => {
                const nextParams = new URLSearchParams(currentParams)
                nextParams.set(BRANCH_URL_PARAM, selectedBranchId)
                return nextParams
            }, {replace: true})
        }
    }, [dispatch, policy, selectedBranchId, setSearchParams, urlBranchId])

    const selectBranch = useCallback((branchId?: string) => {
        if (!policy.isReady || (branchId && !isBranchAllowed(branchId, policy))) return
        pendingSelection.current = branchId === urlBranchId ? null : {branchId}
        dispatch(setSelectedBranchId(branchId))
        setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams)
            if (branchId) nextParams.set(BRANCH_URL_PARAM, branchId)
            else nextParams.delete(BRANCH_URL_PARAM)
            nextParams.delete("page")
            return nextParams
        }, {replace: true})
    }, [dispatch, policy, setSearchParams, urlBranchId])

    return {selectedBranchId, selectBranch}
}
